import {
  Body1,
  Body2,
  Button,
  Link,
  Subtitle2,
} from "@fluentui/react-components";
import { Card, CardFooter } from "@fluentui/react-components/unstable";
import { Dispatch, SetStateAction, useContext, useEffect, useState } from "react";
import { NavLink, useParams } from "react-router-dom";
import { IAssignmentDetails } from "../../../services/types/overtimeAssigments";
import {
  dataTimeService,
  dateFormatService,
  translateService,
  overtimeAssigments,
} from "../../../services";
import { applicationTypeEnum, assignmentStatus, documentTypeEnum, overtimeTypeEnum, userRoleEnum } from "../../../utils/enums";
import {
  AssignemntCardRow,
  CenteredLayout,
  ChangeRealizationDateDialog,
  RealizeAssignmentDialog,
  RejectAssignmentDialog,
  UnrealizedAssignmentDialog,
} from "../../../components/layouts";
import { AppButton, AppLoader } from "../../../components/common";
import UserContext from "../../AppRouter/UserContext";
import useStyles from "./AssignmentDetailsStyles";
import appErrorToast from "../../../components/common/AppErrorToast";
import ModifyAssignmentDialog, {
  type ModifyAssignmentEditScope,
} from "../../../components/layouts/ModifyAssignmentDialog";
import { isEmploymentContract } from "../../../utils/contractType";
import { Edit24Regular } from "@fluentui/react-icons";
import { normalizePayoutRate, tryParsePayoutRate } from "../../../utils/payoutRate";
import {
  getChildCompensationMessage,
  GENERATE_APPLICATION_DETAILS_SUFFIX,
  ILinkedCompensationMessage,
} from "../../../utils/linkedCompensation";

type PayoutRateRowState = { show: false } | { show: true; text: string };

function renderContextMessage(
  message: ILinkedCompensationMessage | null,
  styles: ReturnType<typeof useStyles>
) {
  if (!message) {
    return null;
  }

  const variantClass =
    message.variant === "warning"
      ? styles.contextMessageWarning
      : message.variant === "success"
        ? styles.contextMessageSuccess
        : styles.contextMessageInfo;

  return (
    <div className={`${styles.contextMessage} ${variantClass}`}>
      <Body2>{message.text}</Body2>
    </div>
  );
}

function payoutRateRowState(details: IAssignmentDetails | undefined): PayoutRateRowState {
  if (!details) {
    return { show: false };
  }

  const parsed = tryParsePayoutRate(details.payoutRate);
  const isCommissionOvertime =
    details.documentType === documentTypeEnum.Commision &&
    details.applicationType === applicationTypeEnum.Overtime;

  if (isCommissionOvertime) {
    return { show: true, text: parsed ?? "-" };
  }

  if (details.type === overtimeTypeEnum.TimeToPay && parsed) {
    return { show: true, text: parsed };
  }

  return { show: false };
}

const AssignmentDetails = () => {
  const { id, number } = useParams<{ id: string; number: string }>();
  const [assignmentDetails, setAssignmentDetails] = useState<IAssignmentDetails>();
  const [open, setOpen] = useState(false);
  const [modifyOpen, setModifyOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isAccepting, setIsAccepting] = useState(false);
  const { userData } = useContext(UserContext);
  const [realizeDialogOpen, setRealizeDialogOpen] = useState(false);
  const [changeRealizationDateOpen, setChangeRealizationDateOpen] = useState(false);
  const [unrealizedDialogOpen, setUnerealizedDialogOpen] = useState(false);
  const [reload, setReload] = useState(false);
  const styles = useStyles();

  const acceptAssignment = () => {
    setIsAccepting(true);
    const status = assignmentStatus.Accepted;
    const description = "";
    overtimeAssigments.updateAssignment(status, description, id)
      .then(() => {
        setReload(!reload);
      }).finally(() => {
        setIsAccepting(false);
      });
  };

  const goBack = () => {
    window.history.back();
  }

  const rejectAssignment = (rejectionDescription: string, setIsRejecting: Dispatch<SetStateAction<boolean>>) => {
    if (rejectionDescription.length > 1) {
      setIsRejecting(true);
      const status = assignmentStatus.Rejected;
      overtimeAssigments.updateAssignment(status, rejectionDescription, id)
        .then(() => {
          setOpen(false);
          setReload(!reload);
        }).finally(() => {
          setIsRejecting(false);
        });
    } else {
      appErrorToast("Podaj powód odrzucenia");
    }
  };

  useEffect(() => {
    setAssignment();
  }, [reload, id]);

  const setAssignment = () => {
    setIsLoading(true);
    overtimeAssigments.getAssignmentDetails(id).then((result) => {
      setAssignmentDetails(result);
      setIsLoading(false);
    });
  }

  const checkIsCanEdit = () => {
    const isPartiallyRealizedLeaveApplication =
      assignmentDetails?.status === assignmentStatus.PartiallyRealized &&
      assignmentDetails.documentType === documentTypeEnum.Application &&
      assignmentDetails.applicationType === applicationTypeEnum.Leaving;

    if (isPartiallyRealizedLeaveApplication) {
      return false;
    }

    return (
      (userData.data.baseRole === userRoleEnum.Admin &&
        !!assignmentDetails) ||
      (userData.data.baseRole === userRoleEnum.Employee &&
        assignmentDetails?.status === assignmentStatus.New &&
        assignmentDetails.documentType === documentTypeEnum.Application)
    );
  };

  const canEditCommissionAfterRealized =
    userData.data.baseRole === userRoleEnum.Admin &&
    assignmentDetails?.status === assignmentStatus.Realized &&
    assignmentDetails?.documentType === documentTypeEnum.Commision;

  const canEditEmploymentOvertimeAppAfterRealized =
    userData.data.baseRole === userRoleEnum.Admin &&
    assignmentDetails?.status === assignmentStatus.Realized &&
    assignmentDetails?.documentType === documentTypeEnum.Application &&
    assignmentDetails?.applicationType === applicationTypeEnum.Overtime &&
    isEmploymentContract(assignmentDetails.employee?.contractType ?? null);

  const canChangeRealizationDate =
    userData.data.baseRole === userRoleEnum.Admin &&
    assignmentDetails?.status === assignmentStatus.Realized &&
    !!assignmentDetails?.realization;

  const modifyAssignmentEditScope: ModifyAssignmentEditScope = canEditCommissionAfterRealized
    ? "commissionTypeAfterRealized"
    : canEditEmploymentOvertimeAppAfterRealized
      ? "employmentOvertimeAppAfterRealized"
      : "full";

  const payoutRateRow = payoutRateRowState(assignmentDetails);

  const compensationStatus =
    assignmentDetails?.linkedCompensationStatus ??
    assignmentDetails?.generatedAssignment?.status;

  const hasLinkedCompensation =
    assignmentDetails?.hasLinkedCompensation ?? !!assignmentDetails?.generatedAssignment;

  const isLinkedCompensationSettled =
    assignmentDetails?.isLinkedCompensationSettled ??
    assignmentDetails?.generatedAssignment?.status === assignmentStatus.Realized;

  const childCompensationMessage =
    assignmentDetails?.isGenerated && assignmentDetails.parentAssignment
      ? getChildCompensationMessage(
          assignmentDetails.parentAssignment.status,
          assignmentDetails.status
        )
      : null;

  return (
    <CenteredLayout>
      <RejectAssignmentDialog
        open={open}
        setOpen={setOpen}
        rejectAssignment={rejectAssignment}
      />
      {assignmentDetails && (
        <ModifyAssignmentDialog
          open={modifyOpen}
          setOpen={setModifyOpen}
          editScope={modifyAssignmentEditScope}
          formData={{
            employeeId:
              userData.data.baseRole === userRoleEnum.Admin
                ? assignmentDetails.employee.id
                : userData.data.id,
            employeeContractType: assignmentDetails.employee.contractType ?? null,
            assigmentDate: new Date(assignmentDetails.assignmentDate),
            duration: assignmentDetails.duration.toString(),
            type:
              userData.data.baseRole === userRoleEnum.Admin
                ? assignmentDetails.type === "ToPay"
                  ? "Wypłata"
                  : "Do odbioru"
                : null,
            applicationType: assignmentDetails.applicationType,
            documentType: assignmentDetails.documentType,
            status: assignmentDetails.status,
            assignmentDescription: assignmentDetails?.assignmentDescription
              ? assignmentDetails.assignmentDescription
              : "",
            generateApplication: assignmentDetails.generateApplication,
            payoutRate:
              assignmentDetails.type === overtimeTypeEnum.TimeToPay
                ? normalizePayoutRate(assignmentDetails.payoutRate)
                : "100",
            youTrackProjectId: assignmentDetails.youTrackProjectId,
            youTrackProjectLabel: assignmentDetails.youTrackProjectLabel,
          }}
          refresh={setAssignment}
        />
      )}
      <div className={styles.cardsContainer}>
        <Card className={styles.card}>
          <div className={styles.headerContainer}>
            <Button
              appearance="primary"
              onClick={goBack}
              className={styles.button}
            >
              Powrót
            </Button>
            {(checkIsCanEdit() ||
              canEditCommissionAfterRealized ||
              canEditEmploymentOvertimeAppAfterRealized) && (
              <Edit24Regular
                className={styles.editBtn}
                onClick={() => {
                  setModifyOpen(true);
                }}
              />
            )}
          </div>
          <h2>{assignmentDetails?.documentType === documentTypeEnum.Commision ? "Zlecenie" : "Wniosek"} nr {parseInt(number) + 1}</h2>
          <Body1 className={styles.cardBodyContainer}>
            <Body2 className={styles.cardBody}>
              {isLoading ? (
                <AppLoader />
              ) : (
                <>
                  <AssignemntCardRow
                    title="Pracownik"
                    txt={`${assignmentDetails?.employee.lastName} ${assignmentDetails?.employee.firstName}`}
                  />
                  {assignmentDetails?.applicationType !== applicationTypeEnum.Leaving && (
                    <AssignemntCardRow
                      title="Projekt (YouTrack)"
                      txt={assignmentDetails?.youTrackProjectLabel ?? "—"}
                    />
                  )}
                  <AssignemntCardRow
                    title="Data utworzenia"
                    txt={
                      assignmentDetails &&
                      dateFormatService.formatDate(assignmentDetails.createTime)
                    }
                  />
                  <AssignemntCardRow
                    title="Data realizacji"
                    txt={
                      assignmentDetails &&
                      dateFormatService.formatDate(assignmentDetails.assignmentDate)
                    }
                  />
                  <AssignemntCardRow
                    title="Liczba godzin"
                    txt={dataTimeService.convertFormat(
                      assignmentDetails?.duration
                    )}
                  />

                  {payoutRateRow.show && (
                    <AssignemntCardRow title="% wynagrodzenia" txt={payoutRateRow.text} />
                  )}

                  {assignmentDetails?.applicationType !== applicationTypeEnum.Leaving &&
                    <AssignemntCardRow
                      title="Typ"
                      txt={translateService.translateType(
                        assignmentDetails?.type
                      )}
                    />}
                  {assignmentDetails?.documentType === documentTypeEnum.Application &&
                    <AssignemntCardRow
                      title="Rodzaj wniosku"
                      txt={`${translateService.translateAppplicationType(
                        assignmentDetails?.applicationType
                      )}${assignmentDetails?.generateApplication ? ` ${GENERATE_APPLICATION_DETAILS_SUFFIX}` : ""}`}
                    />}
                  {assignmentDetails?.assignmentDescription && (
                    <div className={styles.assignmentDescription}>
                      <Subtitle2>Opis:</Subtitle2>
                      {assignmentDetails?.assignmentDescription}
                    </div>
                  )}
                  {assignmentDetails?.generatedAssignment && (
                    <div className={styles.assignmentDescription}>
                      <Subtitle2>Rozliczenie wyjścia:</Subtitle2>
                      <NavLink
                        to={`/assignment-details/${assignmentDetails?.generatedAssignment.id}/${number}`}
                      >
                        Przekieruj do rozliczenia odpracowania tego wyjścia
                      </NavLink>
                    </div>
                  )}
                  {assignmentDetails?.isGenerated && assignmentDetails.parentAssignment && (
                    <div className={styles.assignmentDescription}>
                      <Subtitle2>Powiązane wyjście:</Subtitle2>
                      <NavLink
                        to={`/assignment-details/${assignmentDetails.parentAssignment.id}/${number}`}
                      >
                        Przejdź do wniosku o wyjście
                      </NavLink>
                    </div>
                  )}
                  {hasLinkedCompensation && compensationStatus && (
                    <AssignemntCardRow
                      title="Status odpracowania"
                      txt={translateService.translateStatus(compensationStatus)}
                    />
                  )}
                  {hasLinkedCompensation &&
                    !isLinkedCompensationSettled &&
                    renderContextMessage(
                      {
                        text: "Odpracowanie nie zostało jeszcze rozliczone — pula godzin nie została przywrócona.",
                        variant: "warning",
                      },
                      styles
                    )}
                  {renderContextMessage(childCompensationMessage, styles)}
                  <AssignemntCardRow
                    title="Status"
                    txt={translateService.translateStatus(
                      assignmentDetails?.status
                    )}
                  />
                  {assignmentDetails?.rejectionDescription && (
                    <div className={styles.assignmentDescription}>
                      <Subtitle2>Powód:</Subtitle2>
                      {assignmentDetails?.rejectionDescription}
                    </div>
                  )}
                  {((userData.data.baseRole === userRoleEnum.Admin &&
                    assignmentDetails?.status === assignmentStatus.New &&
                    assignmentDetails?.documentType === documentTypeEnum.Application
                  ) ||
                    (assignmentDetails?.documentType === documentTypeEnum.Commision &&
                      assignmentDetails?.status === assignmentStatus.New &&
                      userData.data.baseRole === userRoleEnum.Employee)) && (
                      <CardFooter className={styles.btnsContainer}>
                        <AppButton
                          text="Akceptuj"
                          disabled={isAccepting}
                          appearance="primary"
                          onClick={acceptAssignment}
                        />
                        <Button onClick={() => setOpen(true)}>Odrzuć</Button>
                      </CardFooter>
                    )}
                  {assignmentDetails?.status === assignmentStatus.Accepted &&
                    userData.data.baseRole !== userRoleEnum.Admin && (
                      <CardFooter className={styles.btnsContainer}>
                        <Button
                          appearance="primary"
                          onClick={() => setRealizeDialogOpen(true)}
                        >
                          Rozlicz
                        </Button>
                        <Button onClick={() => setUnerealizedDialogOpen(true)}>
                          Nie zrealizowano
                        </Button>
                      </CardFooter>
                    )}
                </>
              )}
            </Body2>
          </Body1>
        </Card>
        {assignmentDetails?.realization && (
          <Card className={styles.realizationCard}>
            <Body1>
              <Body2 className={styles.cardBody}>
                <>
                  <AssignemntCardRow
                    title="Data realizacji"
                    txt={dateFormatService.formatDate(
                      assignmentDetails.realization.realizationDate
                    )}
                  />
                  <AssignemntCardRow
                    title="Zrealizowano"
                    txt={dataTimeService.convertFormat(
                      assignmentDetails.realization.duration
                    )}
                  />
                  {assignmentDetails.realization.urls.length > 0 && (
                    <>
                      <AssignemntCardRow title="Zrealizowane zadania" />
                      <ul className={styles.listItem}>
                        {assignmentDetails.realization.urls &&
                          assignmentDetails.realization.urls.map((e, index) => {
                            return (
                              <li key={index}>
                                <Link appearance="subtle" target="_blank" href={e.url}>
                                  {e.url}
                                </Link>
                              </li>
                            );
                          })}
                      </ul>
                    </>
                  )}
                  {canChangeRealizationDate && (
                    <CardFooter className={styles.btnsContainer}>
                      <Button
                        appearance="primary"
                        onClick={() => setChangeRealizationDateOpen(true)}
                      >
                        Zmień datę realizacji
                      </Button>
                    </CardFooter>
                  )}
                </>
              </Body2>
            </Body1>
          </Card>
        )}
      </div>
      <RealizeAssignmentDialog
        open={realizeDialogOpen}
        setOpen={setRealizeDialogOpen}
        date={
          assignmentDetails &&
          dateFormatService.formatDate(assignmentDetails.assignmentDate)
        }
        assignmentDuration={dataTimeService.convertFormat(
          assignmentDetails?.duration
        )}
        setReload={setReload}
        reload={reload}
        isCommision={assignmentDetails?.documentType === documentTypeEnum.Commision}
        isOvertime={assignmentDetails?.applicationType === applicationTypeEnum.Overtime}
        isGenerated={assignmentDetails?.isGenerated}
      />
      <ChangeRealizationDateDialog
        open={changeRealizationDateOpen}
        setOpen={setChangeRealizationDateOpen}
        currentRealizationDate={
          assignmentDetails?.realization &&
          dateFormatService.formatDate(
            assignmentDetails.realization.realizationDate
          )
        }
        setReload={setReload}
        reload={reload}
      />
      <UnrealizedAssignmentDialog
        open={unrealizedDialogOpen}
        setOpen={setUnerealizedDialogOpen}
        setReload={setReload}
        reload={reload}
      />
    </CenteredLayout>
  );
};

export default AssignmentDetails;
